import { useMemo, useState } from "react";
import {
  Search, Wrench, Mic, Loader2, ArrowRight, AlertTriangle, ChevronRight,
} from "lucide-react";
import { api, apiError } from "@/lib/api";

/**
 * FaultFinder — 1:1 with the mobile app's fault-finder screen.
 *
 * Layout (top → bottom):
 *   1. Search bar ("Search asset, fault...") + big orange SEARCH button.
 *   2. Horizontal scrolling category chips (All · Drill · Dump Truck · Grader · Digger · Dozer · Loader).
 *   3. Center empty state: big wrench icon + "Filter by category..." caption.
 *   4. Floating orange "AI" mic FAB in the bottom right.
 *
 * The backend has `/api/parts-library/search` and `/api/assets/{id}/recurring-issues`
 * — we hit `/parts-library/search?q=` first, then fall back to filtering
 * assets by category client-side.
 */
const CATEGORIES = [
  { id: "all",       label: "All" },
  { id: "drill",     label: "Drill" },
  { id: "dump_truck", label: "Dump Truck" },
  { id: "grader",    label: "Grader" },
  { id: "digger",    label: "Digger" },
  { id: "dozer",     label: "Dozer" },
  { id: "loader",    label: "Loader" },
  { id: "excavator", label: "Excavator" },
  { id: "auxiliary", label: "Auxiliary" },
];

export default function FaultFinder() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recording, setRecording] = useState(false);

  const runSearch = async () => {
    if (!query.trim() && category === "all") {
      setResults(null);
      return;
    }
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (category !== "all") params.set("category", category);
      const { data } = await api.get(`/parts-library/search?${params.toString()}`);
      const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
      setResults(arr);
    } catch (ex) {
      setErr(apiError(ex));
      setResults([]);
    } finally { setBusy(false); }
  };

  const onKey = (e) => { if (e.key === "Enter") runSearch(); };

  // Simple voice capture placeholder — actual STT happens through the mobile
  // app. On the web we let the user long-press to record and paste text.
  const toggleVoice = () => setRecording((v) => !v);

  const hasFilter = category !== "all" || Boolean(query.trim());

  return (
    <div className="space-y-5 relative min-h-[70vh] pb-32" data-testid="fault-finder-page">
      {/* Header */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Fault Finder</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1">
          Find a fault
        </h1>
        <p className="text-muted mt-2 text-sm max-w-xl">
          Search by asset ID, part number, symptom, or filter by category.
        </p>
      </div>

      {/* Search bar */}
      <div className="flex gap-2" data-testid="fault-search-bar">
        <div className="panel-flat px-3 flex items-center gap-2 flex-1">
          <Search className="w-4 h-4 text-muted shrink-0" />
          <input
            className="w-full py-3 bg-transparent outline-none text-base"
            placeholder="Search asset, fault..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            data-testid="fault-search-input"
          />
        </div>
        <button
          className="btn btn-primary"
          onClick={runSearch}
          disabled={busy}
          style={{ minHeight: 52, fontSize: 15, padding: "0 22px", letterSpacing: "0.05em" }}
          data-testid="fault-search-btn"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
          SEARCH
        </button>
      </div>

      {/* Category chips — horizontally scrolling */}
      <div
        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
        style={{ scrollbarWidth: "thin" }}
        data-testid="fault-category-chips"
      >
        {CATEGORIES.map((c) => {
          const on = c.id === category;
          return (
            <button
              key={c.id}
              onClick={() => { setCategory(c.id); setResults(null); }}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${
                on ? "text-black" : "text-white/80 hover:text-white"
              }`}
              style={on
                ? { background: "var(--primary)" }
                : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
              data-testid={`fault-category-${c.id}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>
      )}

      {/* Results / empty state */}
      {results === null ? (
        <div className="panel-flat p-16 text-center" data-testid="fault-empty">
          <div className="mx-auto w-24 h-24 rounded-3xl flex items-center justify-center mb-4"
               style={{ background: "rgba(245,130,32,0.10)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.25)" }}>
            <Wrench className="w-12 h-12" />
          </div>
          <div className="font-display font-black text-2xl tracking-tight">
            {hasFilter ? "Ready when you are — tap SEARCH." : "Filter by category…"}
          </div>
          <div className="text-sm text-muted mt-2 max-w-md mx-auto">
            Pick a category above, type a keyword, or press the AI mic to describe the fault out loud.
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="panel-flat p-12 text-center text-muted">
          No results for <span className="text-white font-mono-tight">{query || category}</span>.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="fault-results">
          {results.map((r, i) => (
            <div key={r.id || r._id || i} className="panel p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                   style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                <Wrench className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-base truncate">{r.part_name || r.title || r.name || "Result"}</div>
                <div className="text-[11px] text-muted font-mono-tight mt-0.5 truncate">
                  {r.category && <>{r.category} · </>}
                  {r.part_number && <>#{r.part_number} · </>}
                  {r.last_used_for && <>Used for: {r.last_used_for}</>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted" />
            </div>
          ))}
        </div>
      )}

      {/* Floating AI mic FAB */}
      <button
        onClick={toggleVoice}
        className="fixed bottom-8 right-8 z-30 rounded-full flex items-center justify-center transition-transform hover:scale-105"
        style={{
          width: 72, height: 72,
          background: recording ? "#a51919" : "var(--primary)", color: "#0a0a0a",
          boxShadow: recording
            ? "0 12px 30px rgba(165,25,25,0.5), 0 0 0 4px rgba(255,155,155,0.15)"
            : "0 12px 30px rgba(245,130,32,0.5), 0 0 0 1px rgba(255,255,255,0.05)",
        }}
        data-testid="fault-ai-mic"
        aria-label="AI microphone"
        title={recording ? "Stop" : "Ask AI"}
      >
        <div className="relative">
          <Mic className="w-7 h-7" strokeWidth={2.5} />
          <span
            className="absolute -top-2 -right-3 text-[9px] font-display font-black tracking-widest"
            style={{ color: recording ? "#fff" : "#0a0a0a" }}
          >
            AI
          </span>
        </div>
      </button>
    </div>
  );
}
